import get from 'lodash-es/get';
import { mapChartToSeriesData, mapRangeToText } from '@fourthwall/module-analytics';
import { REPORT_TYPES } from '../../../../../consts';
import { prepareSingleValueChart } from '../../../utils/prepareSingleValueChart';
import { createChartDataConversionRates } from './createChartDataConversionRates';
import { mapCurrentProfitDataToChartSeriesData } from './mapCurrentProfitDataToChartSeriesData';
export const createChart = (analyticsChartQuery, membershipsChartQuery, shopsStatsTrafficQuery, params, options, reportType) => {
    const { analyticsChartQuery: analyticsChartQueryName, chartValueQuery: chartValueQueryName, getDateFormatter, formatValue, endpoint, } = REPORT_TYPES[reportType];
    const query = endpoint === 'membership-chart' ? membershipsChartQuery : analyticsChartQuery;
    if (!analyticsChartQueryName || !getDateFormatter || !formatValue)
        return undefined;
    const chartValueData = chartValueQueryName
        ? get(query.data?.current, [chartValueQueryName], undefined)
        : undefined;
    const reportCurrent = get(query.data?.current, [analyticsChartQueryName], undefined);
    const getValue = () => {
        const data = chartValueData || reportCurrent;
        if (!data)
            return undefined;
        const value = data.aggregatedValue ?? data.value ?? 0; // NOTE: aggregatedValue can be null
        if ('currency' in data) {
            return { value, currency: data.currency };
        }
        if (data.aggregatedValue.hasOwnProperty('value')) {
            return data.aggregatedValue.value;
        }
        return value;
    };
    if (reportType === REPORT_TYPES['memberships-members-change'].slug) {
        const getValueChange = () => {
            const value = getValue();
            return value <= 0 ? value : `+${value}`;
        };
        return {
            title: REPORT_TYPES[reportType].title,
            value: getValueChange(),
            description: membershipsChartQuery.data
                ? mapRangeToText(membershipsChartQuery.data.current.range)
                : undefined,
            series: [
                {
                    label: REPORT_TYPES[reportType].title,
                    data: mapCurrentProfitDataToChartSeriesData(prepareSingleValueChart(reportCurrent?.data), params),
                },
            ],
            formatDate: getDateFormatter(params.precision, params.from, params.to),
            formatValue,
            isLoading: membershipsChartQuery.isLoading,
            isError: membershipsChartQuery.isError,
        };
    }
    if (reportType === REPORT_TYPES['memberships-free-members-change'].slug) {
        const getValueChange = () => {
            const value = getValue();
            return value <= 0 ? value : `+${value}`;
        };
        return {
            title: REPORT_TYPES[reportType].title,
            value: getValueChange(),
            description: membershipsChartQuery.data
                ? mapRangeToText(membershipsChartQuery.data.current.range)
                : undefined,
            series: [
                {
                    label: REPORT_TYPES[reportType].title,
                    data: mapCurrentProfitDataToChartSeriesData(prepareSingleValueChart(reportCurrent?.data), params),
                },
            ],
            formatDate: getDateFormatter(params.precision, params.from, params.to),
            formatValue,
            isLoading: membershipsChartQuery.isLoading,
            isError: membershipsChartQuery.isError,
        };
    }
    if (reportType === REPORT_TYPES['memberships-total-members'].slug) {
        return {
            title: REPORT_TYPES[reportType].title,
            value: getValue(),
            description: membershipsChartQuery.data
                ? mapRangeToText(membershipsChartQuery.data.current.range)
                : undefined,
            series: [
                {
                    label: REPORT_TYPES[reportType].title,
                    data: mapCurrentProfitDataToChartSeriesData(reportCurrent?.data, params),
                },
            ],
            formatDate: getDateFormatter(params.precision, params.from, params.to),
            formatValue,
            isLoading: membershipsChartQuery.isLoading,
            isError: membershipsChartQuery.isError,
        };
    }
    if (reportType === REPORT_TYPES['memberships-total-free-members'].slug) {
        return {
            title: REPORT_TYPES[reportType].title,
            value: getValue(),
            description: membershipsChartQuery.data
                ? mapRangeToText(membershipsChartQuery.data.current.range)
                : undefined,
            series: [
                {
                    label: REPORT_TYPES[reportType].title,
                    data: mapCurrentProfitDataToChartSeriesData(reportCurrent?.data, params),
                },
            ],
            formatDate: getDateFormatter(params.precision, params.from, params.to),
            formatValue,
            isLoading: membershipsChartQuery.isLoading,
            isError: membershipsChartQuery.isError,
        };
    }
    if (reportType === REPORT_TYPES['conversion-rates'].slug) {
        const mainSeriesData = mapChartToSeriesData(createChartDataConversionRates(shopsStatsTrafficQuery.data?.current.conversionRates?.data), params, { ...options, addZeroEntriesForMissingDates: false });
        const hasPredictedData = Boolean(shopsStatsTrafficQuery.data?.current.conversionRates?.predictedData?.timestamps?.length);
        const series = [
            {
                label: REPORT_TYPES[reportType].title,
                data: mainSeriesData,
            },
        ];
        if (hasPredictedData) {
            const predictedSeriesData = [
                mainSeriesData[mainSeriesData.length - 1],
                ...mapChartToSeriesData(createChartDataConversionRates(shopsStatsTrafficQuery.data?.current.conversionRates?.predictedData), params, { ...options, addZeroEntriesForMissingDates: false }),
            ];
            series.push({
                label: `${REPORT_TYPES[reportType].title} (predicted)`,
                data: predictedSeriesData,
            });
        }
        return {
            title: REPORT_TYPES[reportType].title,
            value: `${shopsStatsTrafficQuery.data?.current.conversionRates?.aggregatedValue?.checkoutPaid.percentage}%`,
            description: shopsStatsTrafficQuery.data
                ? mapRangeToText(shopsStatsTrafficQuery.data.current.conversionRates?.range)
                : undefined,
            series,
            formatDate: getDateFormatter(params.precision, params.from, params.to),
            formatValue,
            isLoading: shopsStatsTrafficQuery.isFetching,
            isError: shopsStatsTrafficQuery.isError,
        };
    }
    if (reportType === REPORT_TYPES['unique-visitors'].slug) {
        const mainSeriesData = mapChartToSeriesData(shopsStatsTrafficQuery.data?.current.uniqueVisitors?.data, params, { ...options, addZeroEntriesForMissingDates: false });
        const hasPredictedData = Boolean(shopsStatsTrafficQuery.data?.current.uniqueVisitors?.predictedData.timestamps.length);
        const series = [
            {
                label: REPORT_TYPES[reportType].title,
                data: mainSeriesData,
            },
        ];
        if (hasPredictedData) {
            const predictedSeriesData = [
                mainSeriesData[mainSeriesData.length - 1],
                ...mapChartToSeriesData(shopsStatsTrafficQuery.data?.current.uniqueVisitors?.predictedData, params, { ...options, addZeroEntriesForMissingDates: false }),
            ];
            series.push({
                label: `${REPORT_TYPES[reportType].title} (predicted)`,
                data: predictedSeriesData,
            });
        }
        return {
            title: REPORT_TYPES[reportType].title,
            value: shopsStatsTrafficQuery.data?.current.uniqueVisitors?.aggregatedValue,
            description: shopsStatsTrafficQuery.data
                ? mapRangeToText(shopsStatsTrafficQuery.data.current.uniqueVisitors?.range)
                : undefined,
            series,
            formatDate: getDateFormatter(params.precision, params.from, params.to),
            formatValue,
            minSecondaryAxesValue: 0,
            isLoading: shopsStatsTrafficQuery.isFetching,
            isError: shopsStatsTrafficQuery.isError,
        };
    }
    return {
        title: REPORT_TYPES[reportType].title,
        value: getValue(),
        description: analyticsChartQuery.data
            ? mapRangeToText(analyticsChartQuery.data.current.range)
            : undefined,
        series: [
            {
                label: REPORT_TYPES[reportType].title,
                data: mapCurrentProfitDataToChartSeriesData(reportCurrent?.data, params),
            },
        ],
        formatDate: getDateFormatter(params.precision, params.from, params.to),
        formatValue,
        isLoading: analyticsChartQuery.isLoading,
        isError: analyticsChartQuery.isError,
    };
};
